/**
 * SafetyEventsQueue — table of open safety events.
 *
 * Displays events from GET /safety-events?status=open. The "Acknowledge"
 * action is intentionally inert: there is no ack endpoint in the contract yet.
 * See the TODO below.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { ShieldAlert, RefreshCw, Loader2, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { behavioralAssessmentApi, type SafetyEvent } from './types';

interface SafetyEventsQueueProps {
  /** Optional click-through when a clinician opens the related administration. */
  onOpenAdministration?: (administrationId: string) => void;
}

function severityVariant(sev: string): 'destructive' | 'secondary' | 'outline' {
  const s = sev.toLowerCase();
  if (s === 'high' || s === 'critical' || s === 'severe') return 'destructive';
  if (s === 'medium' || s === 'moderate') return 'secondary';
  return 'outline';
}

function formatTs(ts: string): string {
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? ts : d.toLocaleString();
}

export const SafetyEventsQueue: React.FC<SafetyEventsQueueProps> = ({
  onOpenAdministration,
}) => {
  const [events, setEvents] = useState<SafetyEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Local-only acknowledgement state until a real ack endpoint exists.
  const [ackedIds, setAckedIds] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await behavioralAssessmentApi.listSafetyEvents('open');
      setEvents(res.events);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load safety events.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleAck = useCallback((id: string) => {
    // TODO: no acknowledge endpoint exists yet. When the backend adds e.g.
    // POST /safety-events/{id}/acknowledge, wire it here and refresh the queue.
    setAckedIds((prev) => new Set(prev).add(id));
  }, []);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5 text-red-600" aria-hidden="true" />
          Open safety events
        </CardTitle>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
          )}
          Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {error ? (
          <p className="py-6 text-center text-sm text-red-600">{error}</p>
        ) : loading ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>
        ) : events.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            No open safety events. 🎉
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Triggered</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead>Detail</TableHead>
                  <TableHead>Administration</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((ev) => {
                  const acked = ackedIds.has(ev.id);
                  return (
                    <TableRow key={ev.id} className={acked ? 'opacity-50' : undefined}>
                      <TableCell className="whitespace-nowrap text-sm">
                        {formatTs(ev.triggered_at)}
                      </TableCell>
                      <TableCell className="text-sm font-medium">
                        {ev.trigger_type}
                      </TableCell>
                      <TableCell>
                        <Badge variant={severityVariant(ev.severity)}>{ev.severity}</Badge>
                      </TableCell>
                      <TableCell className="text-sm tabular-nums">
                        {ev.item_number ?? '—'}
                      </TableCell>
                      <TableCell className="max-w-xs text-sm">{ev.detail}</TableCell>
                      <TableCell className="text-sm">
                        {onOpenAdministration ? (
                          <button
                            type="button"
                            className="text-blue-600 underline-offset-2 hover:underline"
                            onClick={() => onOpenAdministration(ev.administration_id)}
                          >
                            Open
                          </button>
                        ) : (
                          <span className="font-mono text-xs text-muted-foreground">
                            {ev.administration_id.slice(0, 8)}…
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={acked}
                          onClick={() => handleAck(ev.id)}
                          title="Acknowledge (local only — no backend endpoint yet)"
                        >
                          {acked ? (
                            <>
                              <CheckCircle2 className="mr-1 h-4 w-4" aria-hidden="true" />
                              Acknowledged
                            </>
                          ) : (
                            'Acknowledge'
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
